// Phase 2.0 Lite — Internal Template Preview
// Route: /studio/templates/:templateId/preview
// Fetches a saved internal template and renders its captured v2_content
// using the existing TemplateRenderer. NOT a separate renderer.
// This is admin-only — public users get nothing.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Loader2 } from 'lucide-react';
import TemplateRenderer from '../components/renderer/TemplateRenderer';
import { V2_TEMPLATES, DEFAULT_V2_TEMPLATE_ID } from '../lib/utils/v2Templates';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InternalTemplatePreviewPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!templateId || !token) return;
    setLoading(true);
    axios
      .get(`${API}/api/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setTemplate(r.data))
      .catch((err) => {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Template not found');
      })
      .finally(() => setLoading(false));
  }, [templateId, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950"
           data-testid="template-preview-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950"
           data-testid="template-preview-not-found">
        <div className="text-center">
          <h1 className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-1">
            {error || 'Template not found'}
          </h1>
          <Link to="/studio/templates" className="text-xs text-violet-600 hover:underline">
            ← Back to library
          </Link>
        </div>
      </div>
    );
  }

  const snap = template.snapshot || {};
  const baseId = snap.v2_template_id || template.base_template_id || DEFAULT_V2_TEMPLATE_ID;
  const config = V2_TEMPLATES[baseId];

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950"
           data-testid="template-preview-unsupported">
        <div className="text-center">
          <h1 className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-1">
            Unsupported base template
          </h1>
          <p className="text-xs text-gray-500">base_template_id: {baseId}</p>
          <Link to="/studio/templates" className="text-xs text-violet-600 hover:underline mt-3 inline-block">
            ← Back to library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="template-preview-page">
      {/* Floating preview ribbon */}
      <div
        className="fixed top-3 left-3 right-3 sm:left-4 sm:right-auto z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-black/80 text-white text-[11px] shadow-lg backdrop-blur"
        data-testid="template-preview-ribbon"
      >
        <button
          onClick={() => navigate('/studio/templates')}
          className="-ml-1 p-0.5 hover:bg-white/10 rounded"
          aria-label="Back to library"
          data-testid="template-preview-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="font-medium" data-testid="template-preview-name">
          Preview · {template.name}
        </span>
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/15 uppercase tracking-wider">
          {template.status || 'draft'}
        </span>
      </div>

      <div className="min-h-screen" data-testid="template-preview-render-host">
        <TemplateRenderer
          templateConfig={config}
          invitationData={snap.v2_content || {}}
          themeOverrides={snap.v2_theme_overrides || {}}
          settingsOverrides={{
            ...(snap.v2_settings_overrides || {}),
            // Demo-only safe placeholders so date/venue widgets render
            // something even when the template is mostly blank.
            eventDate: '',
            venueName: '',
          }}
          slug={`preview-${template.slug || template.id}`}
          layoutVersion={template.layout_version || snap.layout_version || 'legacy-flat'}
        />
      </div>
    </div>
  );
}
